import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook } from 'lucide-react';
import { useContent } from '../../contexts/ContentContext';

const LOGO_URL = "/images/pool-service-logo-white.png";

export const Footer = () => {
  const { global } = useContent();

  return (
    <footer data-testid="footer" className="relative bg-[#0F172A] text-white mt-0">
      {/* Swoop Shape */}
      <div className="absolute top-0 left-0 right-0 -translate-y-[99%] pointer-events-none">
        <svg viewBox="0 0 1200 80" preserveAspectRatio="none" className="w-full h-10 md:h-16 block">
          <path
            d="M0,80 L0,50 C200,0 400,10 600,30 C800,50 1000,10 1200,20 L1200,80 Z"
            fill="#0F172A"
          />
        </svg>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-6 sm:pt-10 sm:pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {/* Company Info */}
          <div>
            <img
              src={LOGO_URL}
              alt="A-Quality Pool Service"
              className="h-14 w-auto mb-4"
              width={80}
              height={56}
              loading="lazy"
            />
            <p className="text-slate-300 text-sm mb-3">
              A Cody Pools Company, providing premium pool services to the Central & Gulf Coast Florida area for over 20 years.
            </p>
            <div className="flex space-x-3">
              <a
                href="https://www.facebook.com/profile.php?id=100069451205316"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="p-2 bg-slate-800 rounded-full hover:bg-sky-500 transition-colors"
              >
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-base font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/"
                  data-testid="footer-home"
                  className="text-slate-300 hover:text-sky-400 transition-colors"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  data-testid="footer-services"
                  className="text-slate-300 hover:text-sky-400 transition-colors"
                >
                  Services
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  data-testid="footer-about"
                  className="text-slate-300 hover:text-sky-400 transition-colors"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  data-testid="footer-contact"
                  className="text-slate-300 hover:text-sky-400 transition-colors"
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-base font-semibold mb-4">Our Services</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/services/residential"
                  data-testid="footer-residential"
                  className="text-slate-300 hover:text-sky-400 transition-colors"
                >
                  Residential Pool Cleaning
                </Link>
              </li>
              <li>
                <Link
                  to="/services/repair-maintenance"
                  data-testid="footer-repair"
                  className="text-slate-300 hover:text-sky-400 transition-colors"
                >
                  Pool Repair & Maintenance
                </Link>
              </li>
              <li>
                <Link
                  to="/services/renovations"
                  data-testid="footer-renovations"
                  className="text-slate-300 hover:text-sky-400 transition-colors"
                >
                  Pool Renovations
                </Link>
              </li>
              <li>
                <Link
                  to="/services/commercial"
                  data-testid="footer-commercial"
                  className="text-slate-300 hover:text-sky-400 transition-colors"
                >
                  Commercial Services
                </Link>
              </li>
              <li>
                <Link
                  to="/cities-we-serve"
                  data-testid="footer-cities-we-serve"
                  className="text-slate-300 hover:text-sky-400 transition-colors"
                >
                  Cities We Serve
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-base font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Phone className="h-4 w-4 mr-2 mt-1 text-sky-400 flex-shrink-0" />
                <a
                  href={`tel:${global.phone}`}
                  data-testid="footer-phone"
                  className="text-slate-300 hover:text-sky-400 transition-colors text-sm"
                >
                  {global.phone}
                </a>
              </li>
              <li className="flex items-start">
                <Mail className="h-4 w-4 mr-2 mt-1 text-sky-400 flex-shrink-0" />
                <a
                  href={`mailto:${global.email}`}
                  data-testid="footer-email"
                  className="text-slate-300 hover:text-sky-400 transition-colors text-sm"
                >
                  {global.email}
                </a>
              </li>
              <li className="flex items-start">
                <MapPin className="h-4 w-4 mr-2 mt-1 text-sky-400 flex-shrink-0" />
                <span className="text-slate-300 text-sm">
                  {global.serviceArea}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-6 sm:mt-8 pt-6 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-xs sm:text-sm text-center md:text-left">
              © {new Date().getFullYear()} A-Quality Pool Service. All rights reserved. | License # CPC1460475 | Website Designed By{' '}
              <a 
                href="https://bp.agency" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sky-400 hover:text-sky-300 transition-colors"
              >
                Brand Partners
              </a>
            </p>
            <div className="flex flex-wrap justify-center md:justify-end gap-4 sm:gap-6">
              <Link
                to="/website-accessibility"
                data-testid="footer-accessibility"
                className="text-slate-400 text-xs sm:text-sm hover:text-sky-400 transition-colors"
              >
                WCAG Accessibility Statement
              </Link>
              <Link
                to="/privacy"
                className="text-slate-400 text-xs sm:text-sm hover:text-sky-400 transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-slate-400 text-xs sm:text-sm hover:text-sky-400 transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
